import React, { useState, useRef } from 'react';
import {
  Modal,
  Backdrop,
  Fade,
  CircularProgress,
  IconButton,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import axios from 'axios';
import { globalConfig } from '../configuration/config';
import mainIcon from '../images/main_icon.jpg';
import { useTranslation } from "react-i18next";

const DropzoneModal = ({ onCloseMenu, projectGuid }) => {
  const [open, setOpen] = useState(!!projectGuid); // Открываем, если передан projectGuid
  const [fileError, setFileError] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadMessage, setUploadMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [isUploadComplete, setIsUploadComplete] = useState(false);
  const [isStoFile, setIsStoFile] = useState(false);
  const [stoCheckbox, setStoCheckbox] = useState(false);
  const { t } = useTranslation();

  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const file = e.target.files[0];

    if (file) {
      const fileExtension = file.name.split('.').pop().toLowerCase();
      setSelectedFile(file);
      setFileError('');
      setUploadMessage('');
      setLoading(false);
      setIsUploadComplete(false);
      setUploadProgress(0);
      setIsStoFile(fileExtension === 'sto');
      setStoCheckbox(false);
    }
  };

  const uploadFile = async () => {
    if (!selectedFile) {
      setUploadMessage(t('importStructures.messages.chooseFile'));
      return;
    }

    setLoading(true);
    setUploadMessage('');
    setUploadProgress(0);
    setIsUploadComplete(false);

    const storageCompany = JSON.parse(localStorage.getItem('company')) || {};

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('IdCompany', storageCompany.id || '');
    formData.append('WithEdges', stoCheckbox);
    if (projectGuid) {
      formData.append('ProjectGuid', projectGuid);
    }

    const maxRetries = 3;
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        await axios.post(`${globalConfig.config.common.api}Projects/ConvertFromBazis`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
          withCredentials: true,
          onUploadProgress: (progressEvent) => {
              setUploadProgress(Math.round((progressEvent.loaded * 100) / progressEvent.total));
          },
        });

        setUploadProgress(100); // Явно указываем 100%
        setLoading(false); // Останавливаем индикатор загрузки
        setUploadMessage(
          projectGuid ? (
            t('importStructures.messages.successWithProjectGuid')
          ) : (
            <>
              {t('importStructures.messages.fileUploaded', { file: selectedFile.name })}{' '}
              {t('importStructures.messages.chatWillBeCreatedWithImage')}{' '}
              <img
                src={mainIcon}
                alt="Conversion Icon"
                style={{ width: 50, height: 50, verticalAlign: 'sub', margin: '0 5px' }}
              />{' '}
              {t('importStructures.messages.importProcessShown')}{' '}
              {t('importStructures.messages.resultInSameChat')}
            </>
          )
        );

        setSelectedFile(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
        setIsUploadComplete(true);
        setStoCheckbox(false);
        return;
      } catch (error) {
        console.error(`Attempt ${attempt} failed:`, error);
        setUploadMessage(
          attempt < maxRetries
            ? t('importStructures.messages.retryAttempt', { attempt })
            : t('importStructures.messages.uploadError')
        );
      }
    }

    setLoading(false); // <-- добавил этот вызов в случае ошибки
  };

  const handleOpen = () => setOpen(true);
  const handleClose = () => {
    setOpen(false);
    if (isUploadComplete) onCloseMenu();
    setUploadMessage('');
    setSelectedFile(null);
    setUploadProgress(0);
    setIsUploadComplete(false);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleClose}
        closeAfterTransition
        BackdropComponent={Backdrop}
        BackdropProps={{ timeout: 500 }}
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <Fade in={open}>
          <div
            style={{
              position: 'relative',
              background: '#fff',
              padding: '20px',
              borderRadius: '8px',
              width: '500px',
              maxWidth: '100%',
              overflowY: 'auto',
            }}>
            <IconButton
              aria-label="close"
              style={{ position: 'absolute', right: 10, top: 10, color: '#999', background: 'transparent' }}
              onClick={handleClose}
            >
              <CloseIcon />
            </IconButton>

            <div className="infoList--title">{ t("menu.importStructures") }</div>

            <input
              type="file"
              onChange={handleFileChange}
              ref={fileInputRef}
              style={{ display: 'block', marginBottom: '10px' }}
            />

            {fileError && <p style={{ color: 'red' }}>{fileError}</p>}

            {selectedFile && (
              <p>{t('importStructures.labels.selectedFile', { name: selectedFile.name })}</p>
            )}

            {isStoFile && (
              <FormControlLabel
                control={
                  <Checkbox checked={stoCheckbox} onChange={() => setStoCheckbox(!stoCheckbox)} />
                }
                label={t('importStructures.labels.edges')}
              />
            )}

            {uploadMessage && <p style={{ marginTop: '10px' }}>{uploadMessage}</p>}

            {loading && (
              <div style={{ display: 'flex', alignItems: 'center', marginTop: '10px' }}>
                <CircularProgress size={24} />
                <span style={{ marginLeft: '10px' }}>
                  {t('importStructures.labels.progress', { percent: uploadProgress })}
                </span>
              </div>
            )}

            <div style={{ marginTop: '20px' }}>
              <button
                onClick={isUploadComplete ? handleClose : uploadFile}
                disabled={!isUploadComplete && loading}
              >
                {isUploadComplete ? t('importStructures.buttons.ok') : t('importStructures.buttons.import')}
              </button>
            </div>
          </div>
        </Fade>
      </Modal>

      <div className="bm-item menu-item" onClick={handleOpen}>
        { t("menu.importStructures") }
      </div>
    </>
  );
};

export default DropzoneModal;
